import { L as Logger, ae as post, s as sessionStore, ah as get, p as put, g as getOrganizationStandardUrl, d as getAccessToken, A as ApolloSettingKey, at as AuthSettings, au as caseInsensitiveCompare, c as createStore, e as shouldRefetch, t as getSpaceName, f as getAccountName, F as Features, Y as isStandalone, av as isLocal, G as GlobalEventEmitter, w as LocaleInstanceResolver, aw as getMenuConfig, j as getFeatureFlag, X as Entitlement, ax as defaultServiceOrder, a5 as GlobalPropsInstanceResolver, o as getTenantName, S as STORAGE_KEYS, _ as AppInsightsInstanceResolver, B as BuiltInGroups, ay as AccountType, az as isFalsyOrWhitespace, aA as isNonCloud, aB as defaultActiveItems } from './loc-8e400c4a.js';

function imageBlobToDataUrl(blob) {
    return new Promise((onSuccess) => {
        try {
            const reader = new FileReader();
            reader.onload = function () {
                onSuccess(this.result);
            };
            reader.readAsDataURL(blob);
        }
        catch (e) {
            // fail silently and return empty string
            onSuccess('');
        }
    });
}

class SettingsInstanceResolver {
    static Instantiate(settingsInstance) {
        if (!this._settingsInstance) {
            this._settingsInstance = settingsInstance;
        }
        return this._settingsInstance;
    }
    static get Instance() {
        return this._settingsInstance;
    }
}

const DEFAULT_FAVORITES = [
    'home',
    'orchestrator',
    'studio',
];

async function getEntitlement(organizationStandardUrl, token, entitlements) {
    const entitlementsUrl = `${organizationStandardUrl}/portal_/api/license/accountant/Entitlement/multiple`;
    Logger.debug('api', `fetching entitlements ${entitlements}`);
    const entitlementsResponse = await post(entitlementsUrl, {
        token,
        body: { entitlementNames: entitlements },
    });
    return entitlementsResponse.isEntitled;
}

const getSettingsBaseUrl = () => {
    const organizationStandardUrl = getOrganizationStandardUrl();
    const identityApi = 'portal_/api/identity';
    return `${organizationStandardUrl}/${identityApi}/Setting`;
};
const parseResponse$1 = (response) => response.reduce((acc, setting) => {
    acc[setting.key] = setting.value;
    return acc;
}, {});
async function getSettings(token, keys) {
    if (!keys.length || !token) {
        return {};
    }
    const organizationId = sessionStore.get('organizationId');
    const userId = sessionStore.get('userId');
    const keysString = keys.join('&key=');
    const settingUrl = `${getSettingsBaseUrl()}?key=${keysString}&partitionGlobalId=${organizationId}&userId=${userId}`;
    try {
        const settings = await get(settingUrl, { token });
        return parseResponse$1(settings);
    }
    catch (error) {
        Logger.error(['api'], `error fetching settings: ${error}`);
        return {};
    }
}
async function putSettings(token, settings, userScoped = true) {
    if (!Object.keys(settings).length || !token) {
        return {};
    }
    const organizationId = sessionStore.get('organizationId');
    const userId = sessionStore.get('userId');
    const settingsUrl = `${getSettingsBaseUrl()}`;
    const response = await put(settingsUrl, {
        token,
        body: {
            settings: Object.entries(settings).map(([key, value]) => ({
                key,
                value,
            })),
            partitionGlobalId: organizationId,
            userId: userScoped ? userId : undefined,
        },
    });
    return parseResponse$1(response);
}

class Settings {
    constructor() {
        this.debouncedRequest = null;
        this.settingKeys = new Set();
    }
    /**
     * Fetches settings for the given keys.
     * Debounces requests to reduce the number of requests made to the server.
     *
     * @param keys - The keys to fetch settings for.
     * @returns A promise that resolves to a record of deduped setting keys to their values.
     */
    async getSettings(keys) {
        Logger.debug(['settings'], `queuing settings keys: ${keys}`);
        keys.forEach(key => this.settingKeys.add(key));
        if (!this.debouncedRequest) {
            this.debouncedRequest = new Promise(async (resolve) => setTimeout(Settings.timeoutCallback, Settings.SETTINGS_DEBOUNCE_TIME_MS, this, resolve));
        }
        return await this.debouncedRequest;
    }
    /**
     * Updates settings for the given keys.
     *
     * @param settings - The settings to update.
     * @param userScoped - Whether the settings are user-scoped, when false the settings are org-scoped.
     * @returns A promise that resolves to a record of setting keys to their values.
     */
    static async putSettings(settings, userScoped = true) {
        Logger.debug(['settings'], `putting settings: ${JSON.stringify(Object.keys(settings))}`);
        const token = Settings.validateRequest(Object.keys(settings));
        return await putSettings(token, settings, userScoped);
    }
    static async timeoutCallback(delegate, resolve) {
        delegate.debouncedRequest = null;
        const settings = await Settings.fetchSettings(delegate);
        resolve(settings);
    }
    static async fetchSettings(delegate) {
        Logger.debug(['settings'], `fetching settings: ${delegate.settingKeys}`);
        const keys = Array.from(delegate.settingKeys);
        delegate.settingKeys.clear();
        const token = Settings.validateRequest(keys);
        return await getSettings(token, keys);
    }
    static validateRequest(keys) {
        const organizationStandardUrl = getOrganizationStandardUrl();
        const token = getAccessToken();
        if (!token || !organizationStandardUrl || keys.length < 1) {
            return '';
        }
        return token;
    }
}
Settings.SETTINGS_DEBOUNCE_TIME_MS = 10;

const FavoritesKey = 'Favorites';
const NO_TENANTS = 'NO-TENANTS';
const parseResponse = (settings, tenantId) => settings[ApolloSettingKey.Favorites] ? JSON.parse(settings[ApolloSettingKey.Favorites]) : { [tenantId]: DEFAULT_FAVORITES };
async function getFavoritesSettings() {
    const tenantId = accountAndTenantsStore.get('currentTenant') || NO_TENANTS;
    const organizationId = sessionStore.get('organizationId');
    const userId = sessionStore.get('userId');
    const orgStandardUrl = getOrganizationStandardUrl();
    try {
        if (!orgStandardUrl) {
            Logger.debug(['api'], 'no organizationStandardUrl');
            return { [tenantId]: DEFAULT_FAVORITES };
        }
        if (!organizationId || !userId) {
            Logger.debug(['api'], 'no organizationId or userId');
            return { [tenantId]: DEFAULT_FAVORITES };
        }
        Logger.debug(['api'], 'fetching favorites settings');
        const settings = await SettingsInstanceResolver.Instance.getSettings([ApolloSettingKey.Favorites]);
        return parseResponse(settings, tenantId);
    }
    catch (error) {
        Logger.error(['api'], `error fetching favorites settings: ${error}`);
        return { [tenantId]: DEFAULT_FAVORITES };
    }
}
async function putFavoritesSettings(favoriteServices) {
    const tenantId = accountAndTenantsStore.get('currentTenant');
    const currentFavorites = accountAndTenantsStore.get('favorites');
    currentFavorites[tenantId] = favoriteServices;
    Logger.debug(['api'], 'updating favorites settings');
    const settings = await Settings.putSettings({ [FavoritesKey]: JSON.stringify(currentFavorites) });
    return parseResponse(settings, tenantId);
}

async function getSessionSettings(token) {
    var _a, _b, _c;
    const sessionSettingsUrl = `${getOrganizationStandardUrl()}/portal_/api/identity/Setting?key=${AuthSettings.IdleTimeoutDuration}&key=${AuthSettings.AllowConcurrentBrowserSessions}&partitionGlobalId=${sessionStore.get('organizationId')}`;
    Logger.debug(['api', 'host'], 'fetching session settings');
    const sessionSettings = await get(sessionSettingsUrl, { token }, (e, method, url, requestProps) => {
        Logger.error(['api', 'host'], `unexpected error happened on session settings API call: ${e}`, {
            object: {
                method,
                url,
                requestProps,
                hasToken: !!token,
            },
            stack: e.stack,
        });
    });
    const idleSessionTimeoutPeriod = (_a = sessionSettings === null || sessionSettings === void 0 ? void 0 : sessionSettings.find((setting) => caseInsensitiveCompare(setting.key, AuthSettings.IdleTimeoutDuration))) === null || _a === void 0 ? void 0 : _a.value;
    const allowConcurrentBrowserSessions = (_c = (_b = sessionSettings === null || sessionSettings === void 0 ? void 0 : sessionSettings.find(setting => caseInsensitiveCompare(setting.key, AuthSettings.AllowConcurrentBrowserSessions))) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : 'true';
    return {
        idleSessionTimeoutPeriod: Math.min(
        // if this is not a number, it will return NaN
        Number(idleSessionTimeoutPeriod), 24 * 60 * 60 * 1000) || undefined,
        allowConcurrentSessions: allowConcurrentBrowserSessions,
    };
}
async function getAccount(organizationStandardUrl, token) {
    const accountUrl = `${organizationStandardUrl}/portal_/api/filtering/leftnav/tenantsAndOrganizationInfo`;
    Logger.debug('api', 'fetching account');
    return get(accountUrl, { token }, (e, method, url, requestProps) => {
        const errorIsForbidden = e.name === 'Forbidden';
        if (errorIsForbidden) {
            window.location.assign(`${organizationStandardUrl}/portal_/restricted-policy`);
            return;
        }
        Logger.warn('api', `unexpected error happened on Account API call: ${e}`, {
            object: {
                method,
                url,
                requestProps,
                hasToken: !!token,
            },
            stack: e.stack,
        });
    });
}

async function getUserLicenseManagementStatus(organizationStandardUrl, token) {
    const userLicenseStatusUrl = `${organizationStandardUrl}/portal_/api/userLicense/UserLicense/status`;
    Logger.debug('api', 'fetching user license management status');
    const { enabled } = await get(userLicenseStatusUrl, { token }, (e, method, url, requestProps) => {
        Logger.error('api', `unexpected error happened on user license management status API call: ${e}`, {
            object: {
                method,
                url,
                requestProps,
                hasToken: !!token,
            },
            stack: e.stack,
        });
        return { enabled: false }; // Return false by default if there is an error
    });
    return enabled;
}

async function getCobrandingLogo(logoUrl, token) {
    Logger.debug('api', 'retrieving cobranding logo');
    return get(logoUrl, { token }, (e, method, url, requestProps) => {
        Logger.warn('api', `unexpected error happened on cobranding API call: ${e}`, {
            object: {
                method,
                url,
                requestProps,
                hasToken: !!token,
            },
            stack: e.stack,
        });
    }, (response) => response.blob());
}

function isCobrandingDatabaseStorageEnabled(url) {
    var _a;
    return (_a = url === null || url === void 0 ? void 0 : url.includes('/organization_/api')) !== null && _a !== void 0 ? _a : false;
}
function getCobrandingLogoProperty(theme) {
    var _a, _b;
    const isBaseLightTheme = theme === 'light' || theme === 'light-hc';
    const cobrandingLogoUrl = (_a = accountAndTenantsStore.state.organization) === null || _a === void 0 ? void 0 : _a[!isBaseLightTheme ? 'darkLogo' : 'lightLogo'];
    if (isCobrandingDatabaseStorageEnabled(cobrandingLogoUrl)) {
        return (_b = accountAndTenantsStore.state.organization) === null || _b === void 0 ? void 0 : _b[!isBaseLightTheme ? 'darkLogoDataUrl' : 'lightLogoDataUrl'];
    }
    return cobrandingLogoUrl;
}
async function getCobrandingImageDataUrl(url, token) {
    const blob = await getCobrandingLogo(url, token);
    const dataUrl = await imageBlobToDataUrl(blob);
    return dataUrl;
}
async function hydrateCobrandingImageUrl(account, token) {
    if (!(account === null || account === void 0 ? void 0 : account.organization)) {
        return account;
    }
    const lightLogo = account.organization.lightLogo;
    const darkLogo = account.organization.darkLogo;
    if (isCobrandingDatabaseStorageEnabled(lightLogo)) {
        account.organization.lightLogoDataUrl = await getCobrandingImageDataUrl(lightLogo, token);
    }
    if (isCobrandingDatabaseStorageEnabled(darkLogo)) {
        account.organization.darkLogoDataUrl = await getCobrandingImageDataUrl(darkLogo, token);
    }
    return account;
}

class EventHandlersInstanceResolver {
    static Instantiate(eventHandlersInstance) {
        Logger.debug('EventHandlersInstanceResolver', 'Instantiate', { object: eventHandlersInstance });
        window.PortalShell.EventHandlers = eventHandlersInstance;
        this._eventHandlersInstance = window.PortalShell.EventHandlers;
        return this._eventHandlersInstance;
    }
    static get Instance() {
        return this._eventHandlersInstance;
    }
}

class AccountAndTenants {
    constructor(accountStore) {
        this.accountAndTenantsStore = accountStore;
        this.processAccount();
    }
    /**
     * INTERNAL USE ONLY
     * Handle language change for service order
     */
    handleLanguageChange(locale) {
        Logger.debug('locale', `handling language change for menu items`);
        this.updateMenuItems({ locale });
    }
    /**
     * Call this method to retrigger the /tenantsAndOrganizationInfo call
     * and update all the components with the new values
     *
     * Corresponds to the original RefreshAccountCache implemented in the old shell
     *
     * @param accessToken (optional) The access token to make the call with.
     * @param isHost (optional) Whether or not this is a host account, which is a field in the decoded token
     */
    async fetchAccountAndTenants(forceRefetch = false, accessToken = getAccessToken(), isHost = sessionStore.get('isHost')) {
        var _a, _b, _c;
        Logger.debug('accountAndTenants', 'fetching account and tenants');
        if (await this.handleOnPremApiCall(accessToken, isHost)) {
            this.updateMenuItems();
            return;
        }
        const { refetchCondition, setLocalStorageLastRefetched, } = shouldRefetch(Features.AccountAndTenantsRefetchThrottleMs.name, `${AccountAndTenants.accountAndTenantsLastRefetchedPrefix}_${getSpaceName()}_${getAccountName()}`);
        const shouldFetch = forceRefetch || refetchCondition;
        const organizationStandardUrl = getOrganizationStandardUrl();
        if (shouldFetch && organizationStandardUrl && accessToken) {
            const [account, userLicensingEnabled, entitlements] = await Promise.all([
                getAccount(organizationStandardUrl, accessToken),
                getUserLicenseManagementStatus(organizationStandardUrl, accessToken),
                this.fetchEntitlements(accessToken),
            ]);
            Logger.debug('api', 'account and user license fetch success', {
                object: {
                    account,
                    userLicensingEnabled,
                    entitlements,
                },
            });
            if (account === null || account === void 0 ? void 0 : account.organization) {
                account.organization.userLicensingEnabled = userLicensingEnabled;
                account.organization.entitlements = entitlements;
            }
            const urlNameOrId = isStandalone() || isLocal() ? '' : getAccountName();
            const apiName = (_a = account === null || account === void 0 ? void 0 : account.organization) === null || _a === void 0 ? void 0 : _a.name;
            const apiId = (_b = account === null || account === void 0 ? void 0 : account.organization) === null || _b === void 0 ? void 0 : _b.id;
            if (urlNameOrId && (apiName || apiId) && !((apiName && caseInsensitiveCompare(apiName, urlNameOrId))
                || (apiId && caseInsensitiveCompare(apiId, urlNameOrId)))) {
                Logger.debug('api', `URL does not match API response; urlNameOrId: ${urlNameOrId}, apiName: ${apiName}, apiId: ${apiId}`);
                GlobalEventEmitter.emitSwitchOrganization({
                    newOrg: urlNameOrId,
                    oldOrg: (_c = apiName !== null && apiName !== void 0 ? apiName : apiId) !== null && _c !== void 0 ? _c : '',
                });
                return;
            }
            setLocalStorageLastRefetched();
            const preprocessedAccount = await this.preprocessAccount(account, accessToken);
            this.processAccount(preprocessedAccount, accessToken);
        }
        this.updateMenuItems();
    }
    /**
     * INTERNAL USE ONLY
     * Gets the menu items for the current account, tenant and persona.
     * Pass in the value that caused the menu items to update
     *
     * @internal
     * @returns the menu items as an array of Service objects
     */
    updateMenuItems(_a) {
        var _b, _c, _d, _e, _f;
        var { tenantName = this.accountAndTenantsStore.get('currentTenant'), isHost = sessionStore.get('isHost'), locale = (_b = LocaleInstanceResolver.Instance) === null || _b === void 0 ? void 0 : _b.locale, overrideServices = this.accountAndTenantsStore.get('overrideServices'), isAdmin = this.accountAndTenantsStore.get('isAdmin'), } = _a === void 0 ? {} : _a;
        if (overrideServices.length > 0) {
            this.accountAndTenantsStore.set('overrideServices', overrideServices);
        }
        const menuConfig = getMenuConfig({
            tenantName,
            isHost,
            locale,
        });
        const previewServices = getFeatureFlag(Features.PreviewServices.name);
        if (Array.isArray(previewServices) && previewServices.length > 0) { // This is needed to tests
            for (const previewService of previewServices) {
                const service = menuConfig.services.find(menuService => menuService.id === previewService);
                if (service) {
                    service.showBadge = true;
                    service.isPreview = true;
                }
            }
        }
        const moreMenuItems = [];
        if (isHost) {
            Logger.debug('accountAndTenants', 'returning menu items for host');
            this.accountAndTenantsStore.set('menuItems', menuConfig.services.filter(service => !!service.showInHostAccount));
            return;
        }
        Logger.debug('accountAndTenants', 'returning menu items for non-host');
        const accountServices = defaultActiveItems.concat((_d = (_c = this.accountAndTenantsStore.state.organization) === null || _c === void 0 ? void 0 : _c.accountServices) !== null && _d !== void 0 ? _d : []);
        Logger.debug('store', 'account level services', { object: accountServices });
        const tenant = (_e = this.getTenant(this.accountAndTenantsStore.get('currentTenant'))) !== null && _e !== void 0 ? _e : this.accountAndTenantsStore.get('tenants')[0];
        Logger.debug('store', 'used for menu items', { object: tenant });
        let enabledServices = (_f = tenant === null || tenant === void 0 ? void 0 : tenant.enabledServices) !== null && _f !== void 0 ? _f : [];
        if (!enabledServices.includes('admin') && isAdmin) {
            enabledServices.push('admin');
        }
        const filteredServicesForSAP = getFeatureFlag(Features.FilteredServicesForSAP.name);
        if (this.hasAccountEntitlement(Entitlement.SAP) && (filteredServicesForSAP === null || filteredServicesForSAP === void 0 ? void 0 : filteredServicesForSAP.length) > 0) {
            enabledServices = enabledServices.filter(service => !filteredServicesForSAP.includes(service));
        }
        const activeItems = (isStandalone()) ? overrideServices : enabledServices.concat(accountServices);
        const coreActiveServices = defaultServiceOrder.filter(service => activeItems.includes(service));
        const menuItems = [];
        coreActiveServices.forEach((activeService) => {
            const service = menuConfig.services.find(menuConfigService => caseInsensitiveCompare(menuConfigService.id, activeService));
            if (service) {
                menuItems.push(service);
            }
        });
        this.accountAndTenantsStore.set('menuItems', [
            ...menuItems,
            ...moreMenuItems.map(service => (Object.assign(Object.assign({}, service), { isMore: true }))),
        ]);
    }
    /**
     * Get the current account and tenant info
     * @returns the tenant list as an array of Tenant objects
     */
    getCurrentAccountAndTenantsInfo() {
        var _a;
        return {
            organization: Object.assign({ name: (_a = getAccountName()) !== null && _a !== void 0 ? _a : '' }, this.accountAndTenantsStore.get('organization')),
            tenants: [...this.accountAndTenantsStore.get('tenants')],
            userGroups: [...this.accountAndTenantsStore.get('userGroups')],
            isAdmin: this.accountAndTenantsStore.get('isAdmin'),
        };
    }
    /**
     * Gets the tenant of the current account based on the passed in tenant name or id,
     * or, if none is passed in, the tenant passed to the global properties, the tenant in the
     * current url, or the cached tenant in that priority
     *
     * @param tenantNameOrId
     * @returns the Tenant object
     */
    getTenant(tenantNameOrId) {
        var _a, _b, _c, _d;
        if (tenantNameOrId === void 0) { tenantNameOrId = (_d = (_c = (_b = (_a = GlobalPropsInstanceResolver.Instance) === null || _a === void 0 ? void 0 : _a.devProperties) === null || _b === void 0 ? void 0 : _b.tenant) !== null && _c !== void 0 ? _c : getTenantName()) !== null && _d !== void 0 ? _d : this.getCachedTenantName(); }
        const tenants = this.accountAndTenantsStore.get('tenants');
        if ((tenants === null || tenants === void 0 ? void 0 : tenants.length) < 1) {
            return;
        }
        return tenants.find(tenant => caseInsensitiveCompare(tenant.name, tenantNameOrId)
            || caseInsensitiveCompare(tenant.id, tenantNameOrId));
    }
    /**
     * INTERNAL USE ONLY
     * Gets current favorite services
     *
     * @internal
     */
    getFavorites() {
        const tenantId = accountAndTenantsStore.get('currentTenant') || NO_TENANTS;
        return this.accountAndTenantsStore.get('favorites')[tenantId] || [];
    }
    async fetchFavorites() {
        var _a;
        const orgId = (_a = this.accountAndTenantsStore.get('organization')) === null || _a === void 0 ? void 0 : _a.id;
        const tenants = this.accountAndTenantsStore.get('tenants');
        const { refetchCondition, setLocalStorageLastRefetched, } = shouldRefetch(Features.FavoritesRefetchThrottleMs.name, `${STORAGE_KEYS.PORTAL_SHELL_FAVORITES_DATA_LAST_REFETCHED}-${orgId}`);
        const isTestCloud = this.hasAccountEntitlement(Entitlement.APP_TESTER)
            || this.hasAccountEntitlement(Entitlement.APP_TEST_DEVELOPER);
        const favoritesMap = tenants.reduce((map, tenant) => {
            map[tenant.name] = DEFAULT_FAVORITES;
            return map;
        }, {});
        favoritesMap[NO_TENANTS] = DEFAULT_FAVORITES;
        let favoriteSettings = {};
        try {
            const favoriteString = localStorage.getItem(`${STORAGE_KEYS.PORTAL_SHELL_FAVORITES_DATA}-${orgId}`);
            favoriteSettings = JSON.parse(favoriteString || '');
        }
        catch (_b) {
            Logger.error('accountAndTenants', 'could not parse stored favorites', { printToConsoleOnly: true });
        }
        if (Object.keys(favoriteSettings).length === 0 || refetchCondition) {
            const newFavoriteSettings = await getFavoritesSettings();
            if (Object.keys(newFavoriteSettings).length !== 0) {
                Object.keys(newFavoriteSettings).forEach(key => {
                    var _a, _b;
                    if (Array.isArray(newFavoriteSettings[key]) &&
                        JSON.stringify(newFavoriteSettings[key]) === JSON.stringify(DEFAULT_FAVORITES) &&
                        isTestCloud) {
                        const tenant = (_a = this.accountAndTenantsStore.get('tenants')) === null || _a === void 0 ? void 0 : _a.find(t => t.name === key);
                        if ((_b = tenant === null || tenant === void 0 ? void 0 : tenant.enabledServices) === null || _b === void 0 ? void 0 : _b.includes('testmanager')) {
                            newFavoriteSettings[key] = [...DEFAULT_FAVORITES, 'testmanager'];
                        }
                    }
                });
                favoriteSettings = newFavoriteSettings;
                setLocalStorageLastRefetched();
            }
        }
        Object.keys(favoriteSettings).forEach(key => {
            if (favoritesMap.hasOwnProperty(key)) {
                favoritesMap[key] = favoriteSettings[key];
            }
        });
        localStorage.setItem(`${STORAGE_KEYS.PORTAL_SHELL_FAVORITES_DATA}-${orgId}`, JSON.stringify(favoritesMap));
        this.accountAndTenantsStore.set('favorites', favoritesMap);
        this.accountAndTenantsStore.forceUpdate('favorites');
    }
    async setFavorites(serviceId, isFavorite) {
        var _a, _b;
        const orgId = (_a = this.accountAndTenantsStore.get('organization')) === null || _a === void 0 ? void 0 : _a.id;
        const favoritesMap = this.accountAndTenantsStore.get('favorites');
        const tenantId = accountAndTenantsStore.get('currentTenant') || NO_TENANTS;
        const favoriteServices = (_b = favoritesMap[tenantId]) !== null && _b !== void 0 ? _b : DEFAULT_FAVORITES;
        if (!isFavorite) {
            favoritesMap[tenantId] = [...favoriteServices, serviceId];
        }
        else {
            favoritesMap[tenantId] = favoriteServices.filter(favoriteId => favoriteId !== serviceId);
        }
        localStorage.setItem(`${STORAGE_KEYS.PORTAL_SHELL_FAVORITES_DATA}-${orgId}`, JSON.stringify(favoritesMap));
        this.accountAndTenantsStore.set('favorites', favoritesMap);
        this.accountAndTenantsStore.forceUpdate('favorites');
        await putFavoritesSettings(favoritesMap[tenantId]);
    }
    /**
     * INTERNAL USE ONLY
     * Updates the current tenant of the shell to the passed-in tenant name or id
     *
     * @internal
     * @param tenantNameOrId
     */
    updateCurrentTenant(tenantNameOrId, forceRefresh = false) {
        var _a, _b, _c, _d, _e;
        Logger.debug(['accountAndTenants', 'tenant'], 'updating current tenant', { object: tenantNameOrId });
        const storeTenantName = this.accountAndTenantsStore.get('currentTenant');
        const currentTenant = (_a = this.getTenant(tenantNameOrId)) !== null && _a !== void 0 ? _a : this.accountAndTenantsStore.get('tenants')[0];
        if (!forceRefresh && (!currentTenant || (storeTenantName && caseInsensitiveCompare(currentTenant.name, storeTenantName)))) {
            Logger.debug(['accountAndTenants', 'tenant'], 'no tenant change', {
                object: {
                    storeTenantName,
                    currentTenant,
                },
            });
            // since we return we never fetch the service order so we need to stop waiting for it
            this.accountAndTenantsStore.set('isWaitingForServiceOrder', false);
            return;
        }
        const tenantEvent = {
            id: currentTenant.id,
            name: currentTenant.name,
            selectedTenantId: currentTenant.name,
        };
        const previousTenant = this.accountAndTenantsStore.get('currentTenant');
        if (!previousTenant) {
            GlobalEventEmitter.emitTenantLoaded(tenantEvent);
            (_c = (_b = EventHandlersInstanceResolver.Instance) === null || _b === void 0 ? void 0 : _b.onTenantLoaded) === null || _c === void 0 ? void 0 : _c.call(_b, tenantEvent);
        }
        if (!previousTenant || forceRefresh || (currentTenant.name && previousTenant !== currentTenant.name)) {
            this.accountAndTenantsStore.set('currentTenant', currentTenant.name);
            GlobalEventEmitter.emitTenantChanged(tenantEvent);
            (_e = (_d = EventHandlersInstanceResolver.Instance) === null || _d === void 0 ? void 0 : _d.onTenantChanged) === null || _e === void 0 ? void 0 : _e.call(_d, tenantEvent);
        }
        this.updateMenuItems({ tenantName: currentTenant.name });
        AccountAndTenants.setCachedTenant(currentTenant.name);
        AppInsightsInstanceResolver.SetPlatformInfo({
            cloudTenantId: currentTenant.id,
            tenantId: currentTenant.id,
        });
    }
    /**
     * Returns true if the user is an admin
     *
     * @returns true if the user is an admin
     */
    isAdminUserGroup() {
        var _a, _b;
        return (_b = (_a = this.accountAndTenantsStore) === null || _a === void 0 ? void 0 : _a.get('userGroups').includes(BuiltInGroups.Admin)) !== null && _b !== void 0 ? _b : false;
    }
    /**
     * Returns true if the resource center is hidden for the org
     *
     * @returns true if the resource center is hidden for the org
     */
    isResourceCenterHiddenForNonAdmins() {
        var _a, _b;
        return (_b = (_a = this.accountAndTenantsStore.get('organization')) === null || _a === void 0 ? void 0 : _a.resourceCenterHidden) !== null && _b !== void 0 ? _b : false;
    }
    getCachedAccount() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        // Disabling the rule here to simplify because we don't want to consider empty strings either
        // Prefer ID over name for safety
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
        const accountNameOrId = ((_a = this.accountAndTenantsStore.get('organization')) === null || _a === void 0 ? void 0 : _a.id)
            || ((_b = this.accountAndTenantsStore.get('organization')) === null || _b === void 0 ? void 0 : _b.name)
            || getAccountName();
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
        if (!accountNameOrId) {
            return;
        }
        const cachedAccountString = localStorage.getItem(AccountAndTenants.accountCacheKey);
        try {
            const cachedAccount = JSON.parse(cachedAccountString !== null && cachedAccountString !== void 0 ? cachedAccountString : '');
            const isMatch = caseInsensitiveCompare((_d = (_c = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : '', accountNameOrId)
                || caseInsensitiveCompare((_f = (_e = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _e === void 0 ? void 0 : _e.id) !== null && _f !== void 0 ? _f : '', accountNameOrId);
            if (isMatch) {
                return cachedAccount;
            }
            Logger.warn('cache', `failed to retrieve cache; accountNameOrId: ${accountNameOrId}, cachedAccountName: ${(_g = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _g === void 0 ? void 0 : _g.name}, cachedAccountId: ${(_h = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _h === void 0 ? void 0 : _h.id}, caseInsensitiveMatch: ${isMatch}`);
            if (accountNameOrId && (((_j = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _j === void 0 ? void 0 : _j.name) || ((_k = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _k === void 0 ? void 0 : _k.id))) {
                GlobalEventEmitter.emitSwitchOrganization({
                    newOrg: accountNameOrId,
                    oldOrg: (_m = (_l = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _l === void 0 ? void 0 : _l.name) !== null && _m !== void 0 ? _m : (_o = cachedAccount === null || cachedAccount === void 0 ? void 0 : cachedAccount.organization) === null || _o === void 0 ? void 0 : _o.id,
                });
            }
            return;
        }
        catch (_p) {
            return;
        }
    }
    /**
     * Returns true if the account type is Enterprise
     *
     * @returns Returns true if the account type is Enterprise
     */
    isEnterpriseAccount() {
        var _a;
        const accountType = (_a = this.accountAndTenantsStore.get('organization')) === null || _a === void 0 ? void 0 : _a.accountType;
        return accountType && (accountType === AccountType.Enterprise);
    }
    /**
     * Returns true if the entitlement is present
     * @returns array of entitlements
     */
    hasAccountEntitlement(entitlement) {
        var _a, _b, _c;
        const currentEntitlement = (_a = this.accountAndTenantsStore.get('organization')) === null || _a === void 0 ? void 0 : _a.entitlements;
        if (typeof (currentEntitlement) === 'boolean') {
            if (entitlement === Entitlement.SAP) {
                return currentEntitlement;
            }
            return false;
        }
        return !!((_c = (_b = this.accountAndTenantsStore.get('organization')) === null || _b === void 0 ? void 0 : _b.entitlements) === null || _c === void 0 ? void 0 : _c.includes(entitlement));
    }
    async fetchEntitlements(accessToken, forceRefetch = false) {
        if (!getFeatureFlag(Features.EnableSAPEntitlements.name)) {
            Logger.debug('entitlements', 'SAP entitlement is not enabled');
            return [];
        }
        const { refetchCondition, setLocalStorageLastRefetched, } = shouldRefetch(Features.EntitlementsRefetchThrottleMs.name, `${AccountAndTenants.entitlementsLastRefetchedPrefix}_${getAccountName()}`);
        const shouldFetch = forceRefetch || refetchCondition;
        const organizationStandardUrl = getOrganizationStandardUrl();
        if (shouldFetch && organizationStandardUrl && accessToken) {
            const entitlementsResponse = await getEntitlement(organizationStandardUrl, accessToken, [
                Entitlement.SAP,
                Entitlement.APP_TESTER,
                Entitlement.APP_TEST_DEVELOPER,
            ]);
            const enabledEntitlements = Object.entries(entitlementsResponse)
                .filter(([, isEnabled]) => isEnabled)
                .map(([entitlement]) => entitlement);
            localStorage.setItem(`${AccountAndTenants.entitlementsCacheKey}_${getAccountName()}`, JSON.stringify(enabledEntitlements));
            setLocalStorageLastRefetched();
            return enabledEntitlements;
        }
        const cachedEntitlements = localStorage.getItem(`${AccountAndTenants.entitlementsCacheKey}_${getAccountName()}`);
        return cachedEntitlements ? JSON.parse(cachedEntitlements) : [];
    }
    static setCachedAccount(account) {
        if (!account) {
            return;
        }
        localStorage.setItem(AccountAndTenants.accountCacheKey, JSON.stringify(account));
    }
    static setCachedTenant(tenantNameOrId) {
        localStorage.setItem(AccountAndTenants.tenantCacheKey, tenantNameOrId);
    }
    getCachedTenantName() {
        var _a, _b;
        let tenantNameOrId = (_a = localStorage.getItem(AccountAndTenants.tenantCacheKey)) !== null && _a !== void 0 ? _a : '';
        if (isFalsyOrWhitespace(tenantNameOrId)) {
            tenantNameOrId = (_b = getTenantName()) !== null && _b !== void 0 ? _b : '';
        }
        return tenantNameOrId;
    }
    async handleOnPremApiCall(accessToken, isHost) {
        var _a;
        if (!isStandalone() && !isHost) {
            return false;
        }
        const fallBackTenantList = [
            {
                name: this.accountAndTenantsStore.get('currentTenant'),
                id: '',
                status: 'Enabled',
                enabledServices: [], // store.get('overrideServices')
                color: '',
                isCanaryTenant: false,
            },
        ];
        if (isHost && accessToken) {
            const sessionSettings = await getSessionSettings(accessToken);
            Logger.debug(['api', 'host'], 'session settings fetch success', { object: sessionSettings });
            this.accountAndTenantsStore.set('organization', Object.assign(Object.assign({}, (_a = this.accountAndTenantsStore.state.organization) !== null && _a !== void 0 ? _a : { name: 'host' }), sessionSettings));
            this.accountAndTenantsStore.set('tenants', isHost ? [] : fallBackTenantList);
            this.accountAndTenantsStore.set('userGroups', []);
            this.updateIdleSessionTimeout(sessionSettings.idleSessionTimeoutPeriod);
        }
        else { // Standalone case or session flags are not enabled
            this.accountAndTenantsStore.set('tenants', fallBackTenantList);
            this.accountAndTenantsStore.set('currentTenant', fallBackTenantList[0].name);
        }
        return true;
    }
    async preprocessAccount(account, accessToken) {
        if (account && accessToken) {
            account = await hydrateCobrandingImageUrl(account, accessToken);
            /** further processing can be done here */
        }
        return account;
    }
    processAccount(account = this.getCachedAccount(), accessToken) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        this.accountAndTenantsStore.set('organization', account === null || account === void 0 ? void 0 : account.organization);
        this.accountAndTenantsStore.set('tenants', (_a = account === null || account === void 0 ? void 0 : account.tenants) !== null && _a !== void 0 ? _a : []);
        this.accountAndTenantsStore.set('userGroups', (_b = account === null || account === void 0 ? void 0 : account.userGroups) !== null && _b !== void 0 ? _b : []);
        this.accountAndTenantsStore.set('isAdmin', (_c = account === null || account === void 0 ? void 0 : account.isAdmin) !== null && _c !== void 0 ? _c : false);
        if (!account) {
            return;
        }
        if (this.accountAndTenantsStore.get('tenants').length < 1) {
            GlobalEventEmitter.emitTenantsNotAvailable();
            (_e = (_d = EventHandlersInstanceResolver.Instance) === null || _d === void 0 ? void 0 : _d.onTenantsNotAvailable) === null || _e === void 0 ? void 0 : _e.call(_d);
            this.accountAndTenantsStore.set('currentTenant', '');
            // on this path we never call updateCurrentTenant that fetches for the service order
            this.accountAndTenantsStore.set('isWaitingForServiceOrder', false);
        }
        else {
            this.updateCurrentTenant(undefined, true);
        }
        // Handle the case when the idleSessionTimeout updated
        this.updateIdleSessionTimeout((_f = account === null || account === void 0 ? void 0 : account.organization) === null || _f === void 0 ? void 0 : _f.idleSessionTimeoutPeriod);
        // Handle the case for Pendo
        if (account && accessToken && !isNonCloud()) {
            this.updateOrInitializePendo();
        }
        AccountAndTenants.setCachedAccount(account);
        AppInsightsInstanceResolver.SetPlatformInfo({
            subscriptionType: (_g = account === null || account === void 0 ? void 0 : account.organization) === null || _g === void 0 ? void 0 : _g.accountType,
            serverRegion: (_h = account === null || account === void 0 ? void 0 : account.organization) === null || _h === void 0 ? void 0 : _h.region,
        });
        AppInsightsInstanceResolver.SetPlatformMetadata({ supportId: (_k = (_j = account === null || account === void 0 ? void 0 : account.organization) === null || _j === void 0 ? void 0 : _j.licenseCode) !== null && _k !== void 0 ? _k : 'UNLICENSED' });
    }
    updateIdleSessionTimeout(idleSessionTimeoutPeriod) {
        var _a, _b;
        if (!idleSessionTimeoutPeriod) {
            (_a = window.PortalShell) === null || _a === void 0 ? void 0 : _a.Session.cancelIdleTimeout();
        }
        else if (sessionStore.get('isSignInSuccessful')) {
            Logger.debug('idle timeout', 'starting idle timeout from account side effect');
            (_b = window.PortalShell) === null || _b === void 0 ? void 0 : _b.Session.enforceIdleTimeout(idleSessionTimeoutPeriod);
        }
    }
    updateOrInitializePendo() {
        var _a, _b;
        Logger.debug(['accountAndTenants', 'pendo'], 'attempt to update all options');
        (_a = window.PortalShell) === null || _a === void 0 ? void 0 : _a.Pendo.updateOptionsFromStore();
        Logger.debug(['accountAndTenants', 'pendo'], 'attempt init');
        (_b = window.PortalShell) === null || _b === void 0 ? void 0 : _b.Pendo.initializeFromStore();
    }
}
AccountAndTenants.accountCacheKey = STORAGE_KEYS.PORTAL_ACCOUNT;
AccountAndTenants.tenantCacheKey = STORAGE_KEYS.PORTAL_CURRENT_TENANT;
AccountAndTenants.accountAndTenantsLastRefetchedPrefix = STORAGE_KEYS.PORTAL_SHELL_ACCOUNT_AND_TENANTS_LAST_REFETCHED;
AccountAndTenants.entitlementsCacheKey = STORAGE_KEYS.PORTAL_SHELL_ENTITLEMENTS;
AccountAndTenants.entitlementsLastRefetchedPrefix = STORAGE_KEYS.PORTAL_SHELL_ENTITLEMENTS_LAST_REFETCHED;
const accountAndTenantsStore = createStore({
    organization: undefined,
    tenants: [],
    userGroups: [],
    currentTenant: '',
    menuItems: [],
    overrideServices: [],
    isAdmin: false,
    isWaitingForServiceOrder: true,
    isWaitingForFavorites: true,
    favorites: {},
});

const academy = `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none" class="serviceIcon" role="img" aria-labelledby="menu-text-academy" alt="academy" title="academy">
<path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 0L0 6L3.81818 8.18V14.18L10.5 18L17.1818 14.18V8.18L19.0909 7.09V14H21V6L10.5 0ZM17.01 6L10.5 9.72L3.99 6L10.5 2.28L17.01 6ZM10.5 15.72L15.2727 12.99V9.27L10.5 12L5.72727 9.27V12.99L10.5 15.72Z" class="svgFill"/>
</svg>
`;

const actions = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-actions" width="18px" height="18px" viewBox="0 0 18 18" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" alt="actions" title="actions">
      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g class="svgStroke" stroke-width="2">
              <g>
                  <polygon points="2 10.3276512 7.58937328 3 7.58937328 5.92673332 7.58937328 6.72627438 8.3512297 6.88952631 12 7.6723488 6.41062672 15 6.41062672 12.0732667 6.41062672 11.2737256 5.6487703 11.1104737"></polygon>
                  <line x1="10" y1="17" x2="18" y2="17"></line>
                  <line x1="13" y1="12" x2="18" y2="12"></line>
              </g>
          </g>
      </g>
  </svg>
`;

const admin = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-admin" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="admin" title="admin">
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M0 4H18V18H13.5714V16H16V6H2V16H4.4286V18H0V4Z" />
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M4.16943 10.366L5.14483 10.93C5.05043 11.271 5.00003 11.629 5.00003 12C5.00003 12.371 5.05043 12.73 5.14483 13.071L4.16943 13.634L5.16943 15.367L6.14633 14.803C6.64773 15.314 7.28493 15.69 7.99943 15.874L8.00003 17.001H10L10.0006 15.874C10.7151 15.69 11.3523 15.314 11.8537 14.803L12.8306 15.367L13.8306 13.634L12.8552 13.071C12.9496 12.73 13 12.371 13 12C13 11.629 12.9496 11.271 12.8552 10.93L13.8306 10.366L12.8306 8.634L11.8537 9.197C11.3523 8.687 10.7151 8.31 10.0006 8.126L10 7H8.00003L7.99943 8.126C7.28493 8.31 6.64773 8.687 6.14633 9.197L5.16943 8.634L4.16943 10.366ZM9.00003 14C10.1046 14 11 13.105 11 12C11 10.896 10.1046 10 9.00003 10C7.89543 10 7.00003 10.896 7.00003 12C7.00003 13.105 7.89543 14 9.00003 14Z" />
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M0 0H18V6H0V0ZM2 2V4H16V2H2Z" />
</svg>
`;

const agenticorchestration = `<svg width="18" height="18" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" class="serviceIcon" aria-labelledby="menu-text-agenticorchestration" alt="agenticorchestration" title="agenticorchestration">
<path fill-rule="evenodd" clip-rule="evenodd" d="M19.5 15C18.3954 15 17.5 15.8954 17.5 17V18H15.5L15.5 10L17.5 10V11C17.5 12.1046 18.3954 13 19.5 13L24.5 13C25.6046 13 26.5 12.1046 26.5 11V6C26.5 4.89543 25.6046 4 24.5 4L19.5 4C18.3954 4 17.5 4.89543 17.5 6V7L14.5 7C13.3954 7 12.5 7.89543 12.5 9V12.5L10.7711 12.5C10.134 10.4714 8.23882 9 6 9C3.23858 9 1 11.2386 1 14C1 16.7614 3.23857 19 6 19C8.23882 19 10.134 17.5286 10.7711 15.5L12.5 15.5V19C12.5 20.1046 13.3954 21 14.5 21H17.5V22C17.5 23.1046 18.3954 24 19.5 24H24.5C25.6046 24 26.5 23.1046 26.5 22V17C26.5 15.8954 25.6046 15 24.5 15L19.5 15ZM20.5 18V21H23.5V18H20.5ZM20.5 7V10L23.5 10V7L20.5 7ZM8 14C8 15.1046 7.10457 16 6 16C4.89543 16 4 15.1046 4 14C4 12.8954 4.89543 12 6 12C7.10457 12 8 12.8954 8 14Z" class="svgFill" />
</svg>
`;

const agents = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="serviceIcon" aria-labelledby="menu-text-agents" alt="agents" title="agents">
<path fill-rule="evenodd" clip-rule="evenodd" d="M1.50107 5.06273C0.637915 5.28438 0 6.06769 0 7V14C0 15.1046 0.89543 16 2 16H16C17.1046 16 18 15.1046 18 14V7C18 5.89543 17.1046 5 16 5H10.5C10.5 5.68607 10.1155 6.37215 9.34656 6.65669L9.09439 6.75H16C16.1381 6.75 16.25 6.86193 16.25 7V14C16.25 14.1381 16.1381 14.25 16 14.25H2C1.86193 14.25 1.75 14.1381 1.75 14V7C1.75 6.86193 1.86193 6.75 2 6.75H2.90561L2.65344 6.65669C1.90792 6.38082 1.5238 5.72753 1.50107 5.06273Z" class="svgFill"/>
<path d="M8.25 10.375C8.25 9.89175 8.64175 9.5 9.125 9.5C9.60825 9.5 10 9.89175 10 10.375V11.125C10 11.6082 9.60825 12 9.125 12C8.64175 12 8.25 11.6082 8.25 11.125V10.375Z" class="svgFill"/>
<path d="M12 10.375C12 9.89175 12.3918 9.5 12.875 9.5C13.3582 9.5 13.75 9.89175 13.75 10.375V11.125C13.75 11.6082 13.3582 12 12.875 12C12.3918 12 12 11.6082 12 11.125V10.375Z" class="svgFill"/>
<path d="M5.46524 2.37232C5.64893 1.87589 6.35107 1.87589 6.53476 2.37232L7.10004 3.89996L8.62768 4.46524C9.12411 4.64893 9.12411 5.35107 8.62768 5.53476L7.10004 6.10004L6.53476 7.62768C6.35107 8.12411 5.64893 8.12411 5.46524 7.62768L4.89996 6.10004L3.37232 5.53476C2.87589 5.35107 2.87589 4.64893 3.37232 4.46524L4.89996 3.89996L5.46524 2.37232Z" class="svgFill"/>
</svg>
`;

const aifabric = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-aifabric"  width="18px" height="18px" viewBox="0 0 18 18" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" alt="aifabric" title="aifabric">
      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g class="svgStroke" stroke-width="2">
              <g transform="translate(1.000000, 1.000000)">
                  <path d="M16.0009029,13.0001268 C15.9992014,11.3591268 14.6549014,10.0161268 12.9972014,10.0011268 C11.3409014,9.98612677 9.99830143,11.3051268 9.99999982,12.9461268 C10.0016014,14.5871268 11.3459014,15.9291268 13.0021014,15.9441268 C14.6599014,15.9591268 16.0025014,14.6411268 16.0009029,13.0001268 Z"></path>
                  <path d="M6.00090286,13.0001268 C5.99920143,11.3591268 4.65490143,10.0161268 2.99720143,10.0011268 C1.34100143,9.98612677 -0.00159857142,11.3051268 -2.30926389e-14,12.9461268 C0.00160142858,14.5871268 1.34590143,15.9291268 3.00210143,15.9441268 C4.65990143,15.9591268 6.00250143,14.6411268 6.00090286,13.0001268 Z"></path>
                  <path d="M16.0009029,3.00012677 C15.9993014,1.35912677 14.6550014,0.0161267748 12.9972014,0.0011267748 C11.3410014,-0.0138732252 9.99840143,1.30512677 10,2.94612677 C10.0016014,4.58712677 11.3459014,5.92912677 13.0021014,5.94412677 C14.6599014,5.95912677 16.0025014,4.64112677 16.0009029,3.00012677 Z"></path>
                  <polyline points="5.01130143 4.59712677 6.10160143 5.72212677 8.28210143 7.97212677 10.4626014 10.2221268 11.5528014 11.3471268"></polyline>
                  <path d="M0.0014,2.94312677 C0.00300142858,4.58412677 1.34740143,5.92712677 3.00520143,5.94212677 C4.66150143,5.95712677 6.00410143,4.63912677 6.00240304,2.99812677 C6.00080143,1.35712677 4.65650143,0.0151267748 3.00020143,0.000126774799 C1.34240143,-0.0148732252 -0.000198571424,1.30312677 0.0014,2.94312677 Z"></path>
              </g>
          </g>
      </g>
  </svg>
`;

const apps = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-apps" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="apps" title="apps">
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M0 0H8V8H0V0ZM2 2V6H6V2H2Z" />
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M0 10H8V18H0V10ZM2 12V16H6V12H2Z" />
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M10 0H18V8H10V0ZM12 2V6H16V2H12Z" />
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M14.999 14.999V18H12.999V14.999H10V12.999H12.999V10H14.999V12.999H18V14.999H14.999Z" />
</svg>
`;

const assistant = `<svg width="18" height="18" viewBox="0 0 24 34" fill="none" xmlns="http://www.w3.org/2000/svg" class="serviceIcon" aria-labelledby="menu-text-assistant" alt="assistant" title="assistant">
<path d="M18.5625 13.7188V26.8438H23.4844V13.7188H18.5625Z" class="svgFill"/>
<path d="M0.515625 13.7188V26.8438H5.4375V13.7188H0.515625Z" class="svgFill"/>
<path d="M12 8.80431C12.4867 8.80431 12.9625 8.94861 13.3672 9.21901C13.7719 9.48941 14.0874 9.87381 14.2736 10.3234C14.4599 10.7731 14.5086 11.2679 14.4137 11.7453C14.3187 12.2227 14.0843 12.6612 13.7402 13.0053C13.396 13.3495 12.9575 13.5839 12.4801 13.6789C12.0027 13.7738 11.5079 13.7251 11.0582 13.5388C10.6086 13.3525 10.2242 13.0371 9.95379 12.6324C9.68339 12.2277 9.53909 11.7519 9.53909 11.2652C9.53909 10.6125 9.79829 9.98661 10.2599 9.52511C10.7214 9.06351 11.3473 8.80431 12 8.80431Z" class="svgFill"/>
<path d="M12 5.51562C10.6946 5.51562 9.4427 6.0342 8.5197 6.9572C7.5967 7.8802 7.0781 9.1321 7.0781 10.4375H2.1562C2.1562 7.8268 3.1934 5.32298 5.0394 3.47692C6.8855 1.63086 9.3893 0.59375 12 0.59375C14.6107 0.59375 17.1145 1.63086 18.9606 3.47692C20.8066 5.32298 21.8437 7.8268 21.8438 10.4375H16.9219C16.9219 9.1321 16.4033 7.8802 15.4803 6.9572C14.5573 6.0342 13.3054 5.51562 12 5.51562Z" class="svgFill"/>
<path d="M12 33.4062C10.2595 33.4062 8.5903 32.7148 7.3596 31.4841C6.1289 30.2534 5.4375 28.5842 5.4375 26.8438H10.3594C10.3594 27.2789 10.5322 27.6962 10.8399 28.0038C11.1476 28.3115 11.5649 28.4844 12 28.4844C12.4351 28.4844 12.8524 28.3115 13.1601 28.0038C13.4678 27.6962 13.6406 27.2789 13.6406 26.8438H18.5625C18.5625 28.5842 17.8711 30.2534 16.6404 31.4841C15.4097 32.7148 13.7405 33.4062 12 33.4062Z" class="svgFill"/>
</svg>
`;

const automationhub = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-automationhub" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="automation hub" title="automation hub">
    <path d="M5.80425 17L9 11.8868L12.1958 17H5.80425Z" class="svgStroke" stroke-width="2"/>
    <path stroke="none"  class="svgFill" stroke-width="0"  d="M6.84796 10.3427C6.23558 9.36387 6.35652 8.05746 7.20693 7.20704C8.19718 6.21679 9.80269 6.21679 10.7929 7.20704C11.6434 8.05746 11.7643 9.36387 11.1519 10.3427C11.1478 10.3492 11.1437 10.3557 11.1396 10.3622L12.2718 12.1413C12.492 11.9121 12.6841 11.6646 12.8474 11.4035C13.943 9.65234 13.7308 7.31652 12.2072 5.79283C10.4359 4.02153 7.56402 4.02153 5.79272 5.79283C4.26903 7.31652 4.05689 9.65234 5.15245 11.4035C5.31584 11.6646 5.50793 11.9122 5.72824 12.1415L6.8604 10.3624C6.85622 10.3558 6.85207 10.3493 6.84796 10.3427Z"/>
    <path stroke="none" class="svgFill" stroke-width="0" d="M2 9C2 5.13401 5.13401 2 9 2C12.866 2 16 5.13401 16 9C16 11.0958 15.079 12.9765 13.6196 14.2593L14.7034 15.9625C16.7159 14.3119 18 11.8059 18 9C18 4.02944 13.9706 0 9 0C4.02944 0 0 4.02944 0 9C0 11.8059 1.28405 14.3119 3.29661 15.9625L4.38042 14.2593C2.92103 12.9765 2 11.0958 2 9Z"/>
</svg>
`;

const automationstore = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-automationstore" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="automation store" title="automation store">
    <path class="svgFill" fill-rule="evenodd" clip-rule="evenodd" d="M10.8281 1.41406L9.41406 0L8 1.41406L9.41406 2.82812L10.8281 1.41406ZM17.8321 4.55471L16 7.30282V18H2V7.30276L0.167969 4.55471L1.83207 3.44531L3.53519 6H14.4648L16.168 3.44531L17.8321 4.55471ZM14 8.00001H4V16H14V8.00001ZM6.41406 2L7.82812 3.41406L6.41406 4.82812L5 3.41406L6.41406 2ZM13.8281 3.41406L12.4141 2L11 3.41406L12.4141 4.82812L13.8281 3.41406Z"/>
</svg>
`;

const chatbots = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-chatbots" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="chatbots" title="chatbots">
    <path d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 10.0847 1.21587 11.1189 1.60699 12.0622C1.80893 12.5492 2.05759 13.0119 2.34737 13.4448C2.63943 13.881 2.97326 14.287 3.34315 14.6569" class="svgStroke" stroke-width="2"/>
    <path d="M2 16H9V18H0L2 16Z" class="svgFill"/>
    <path d="M12.375 10.125C12.375 11.618 10.9089 12.9375 9 12.9375C7.16176 12.9375 5.7342 11.714 5.63098 10.2901L5.625 10.125H12.375Z" class="svgFill"/>
</svg>
`;

const customerPortal = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-customerportal" alt="admin" title="admin" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16.7071 12.5C17.0976 12.8905 17.0976 13.5237 16.7071 13.9142L14.2071 16.4142C13.832 16.7893 13.3233 17 12.7929 17H5.12132L4.41421 17.7071C4.21895 17.9024 3.96303 18 3.70711 18H0V16.2929L3.41421 12.8787C3.97682 12.3161 4.73988 12 5.53553 12H11.2071C11.2071 13.1046 10.3117 14 9.20711 14H8.70711C8.43096 14 8.20711 14.2239 8.20711 14.5C8.20711 14.7761 8.43096 15 8.7071 15H11.9645C12.4949 15 13.0036 14.7893 13.3787 14.4142L15.2929 12.5C15.6834 12.1095 16.3166 12.1095 16.7071 12.5Z" class="svgFill" />
<circle cx="8.96356" cy="5" r="2" class="svgStroke" stroke-width="2"/>
<path d="M4.63721 10C5.4055 8.81753 7.05396 8 8.96358 8C10.8732 8 12.5217 8.81753 13.2899 10" class="svgStroke" stroke-width="2"/>
</svg>
`;

const dataservice = `<svg role="img" class="serviceIcon" aria-labelledby="menu-text-dataservice" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="data service" title="data service">
    <path d="M1.66667 6.75004L9 1.25L16.3333 6.75004L9 12.25L1.66667 6.75004Z" class="svgStroke" stroke-width="2"/>
    <path d="M17.2996 10.3506L8.99989 16.5943L0.697388 10.3506" class="svgStroke" stroke-width="2"/>
</svg>
`;

const docs = `<svg class="serviceIcon" aria-labelledby="menu-text-docs" alt="Documentation" title="Documentation" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11 0H1.00003L1.00002 1.8V16.2L1 18H17V16.2V5.4L11 0ZM13 9H5.00002V10.8H13V9ZM13 12.6H5.00002V14.4H13V12.6ZM3.00002 16.2H15V6.3H10V1.8H3.00002V16.2Z" class="svgFill" />
</svg>
`;

const du = `<svg class="serviceIcon" aria-labelledby="menu-text-du" width="18" height="18" role="img" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="document understanding" title="document understanding">
    <path d="M18 2C18 1.44772 17.5523 1 17 1H4C3.44771 1 3 1.44772 3 2V5H5V3H16V14.8122L15.4877 14.3688C15.112 14.0437 14.5546 14.0437 14.179 14.3688L12.5806 15.752L11.0444 14.7549C10.7133 14.54 10.2867 14.54 9.95558 14.7549L8.33333 15.8078L6.71109 14.7549C6.37996 14.54 5.95338 14.54 5.62225 14.7549L5 15.1588V13H3V17C3 17.3666 3.20059 17.7038 3.52273 17.8788C3.84487 18.0537 4.23692 18.0384 4.54442 17.8388L6.16667 16.7859L7.78891 17.8388C8.12004 18.0537 8.54662 18.0537 8.87775 17.8388L10.5 16.7859L12.1222 17.8388C12.4957 18.0812 12.9844 18.0475 13.321 17.7562L14.8333 16.4475L16.3456 17.7562C16.6416 18.0123 17.0597 18.0722 17.4157 17.9095C17.7717 17.7468 18 17.3914 18 17V2Z" class="svgFill"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M1 6C0.447715 6 0 6.44772 0 7V11C0 11.5523 0.447715 12 1 12H7C7.55228 12 8 11.5523 8 11V7C8 6.44772 7.55228 6 7 6H1ZM5.37863 8.32677C5.55911 8.11777 5.536 7.80204 5.327 7.62155C5.118 7.44107 4.80226 7.46418 4.62178 7.67318L3.2678 9.24107L2.91382 8.71928C2.75879 8.49076 2.44786 8.43119 2.21934 8.58622C1.99083 8.74125 1.93125 9.05218 2.08628 9.28069L2.80631 10.342C2.89361 10.4707 3.03581 10.5515 3.19104 10.5605C3.34627 10.5695 3.49687 10.5058 3.59851 10.3881L5.37863 8.32677Z" class="svgFill"/>
    <path d="M14 6H10V8H14V6Z" class="svgFill"/>
    <path d="M14 10H10V12H14V10Z" class="svgFill"/>
</svg>
`;

const help = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-help" width="18px" height="18px" viewBox="0 0 18 18" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" alt="help" title="help">
    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
        <g>
            <g>
                <g transform="translate(1.000000, 1.000000)">
                    <path d="M6.0006,6.134 L6.0006,6.067 C6.0006,5.341 6.3787,4.668 7.0006,4.284 C7.6128,3.907 8.3885,3.907 9.0006,4.284 C9.6226,4.668 10.0006,5.341 10.0006,6.067 L10.0006,6.134 C10.0006,6.901 9.6008,7.613 8.9431,8.019 L8.0006,8.6 L8.0006,10.001" class="svgStroke" stroke-width="2"></path>
                    <polygon class="svgFill" fill-rule="nonzero" points="8.9985 11.002 7.0006 11 6.9991 13 8.997 13.002"></polygon>
                    <path d="M8.0014,16.002 C12.42,16.002 16.0017,12.42 16.0014,8.002 L16.0014,8.002 C16.001,3.583 12.4187,0.001 8,0 L8,0 C3.5814,0 -0.0003,3.582 -5.32907052e-15,8 L-5.32907052e-15,8 C0.0004,12.419 3.5827,16.001 8.0014,16.002 L8.0014,16.002 Z" class="svgStroke" stroke-width="2"></path>
                </g>
            </g>
        </g>
    </g>
</svg>
`;

const home = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-home" width="18px" height="18px" viewBox="0 0 18 18" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" alt="home" title="home">
      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g class="svgStroke">
              <g transform="translate(1.000000, 0.000000)">
                  <path d="M8,1.32820701 L1,7.44723951 L1,17 L15,17 L15,7.44723951 L8,1.32820701 Z" stroke-width="2"></path>
                  <rect stroke-width="0.999246" transform="translate(7.996937, 14.671977) rotate(0.043277) translate(-7.996937, -14.671977) " x="7.49731374" y="12.1748767" width="1" height="4.9942"></rect>
              </g>
          </g>
      </g>
  </svg>
`;

const insights = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-insights" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="insights" title="insights">
    <path d="M1 0V17H18" class="svgStroke" stroke-width="2"/>
    <path d="M4 11L8.5 6.5L12 9.5L16 5" class="svgStroke" stroke-width="2"/>
    <path d="M17 5.58579L15.4142 4H17V5.58579Z" class="svgStroke" stroke-width="2"/>
</svg>
`;

const integrations = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-integrations" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="integrations" title="integrations">
    <path d="M7 2H4C2.89543 2 2 2.89543 2 4V7" class="svgStroke" stroke-width="2"/>
    <path d="M11 16H14C15.1046 16 16 15.1046 16 14V11" class="svgStroke" stroke-width="2"/>
    <path d="M5.99998 10L7.99998 8" class="svgStroke" stroke-width="2"/>
    <path d="M7.99998 12L9.99998 10" class="svgStroke" stroke-width="2"/>
    <path d="M1.00002 17L3.00002 15" class="svgStroke" stroke-width="2"/>
    <path d="M4.23172 8.78421L9.15223 13.7047L6.57495 16.282C6.18443 16.6725 5.55126 16.6725 5.16074 16.282L1.65444 12.7757C1.26392 12.3852 1.26392 11.752 1.65444 11.3615L4.23172 8.78421Z" class="svgStroke" stroke-width="2"/>
    <path d="M17 1L15 3" class="svgStroke" stroke-width="2"/>
    <path d="M14.1775 9.14041L9.25603 4.21895L11.834 1.64096C12.2245 1.25044 12.8577 1.25044 13.2482 1.64096L16.7555 5.14821C17.146 5.53874 17.146 6.1719 16.7555 6.56243L14.1775 9.14041Z" class="svgStroke" stroke-width="2"/>
</svg>
`;

const maestro = `<svg width="18" height="18" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="serviceIcon" aria-labelledby="menu-text-maestro" alt="maestro" title="maestro">
<g id="Maestro icon">
<g id="Union">
<path d="M3.56665 5.41429C3.56665 3.09794 6.36722 1.93791 8.00513 3.57582L12.548 8.11871L10.2853 10.3815L6.76665 6.86282V18.2779H3.56665V5.41429Z" class="svgFill"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M15.5707 9.26274C15.9612 8.87222 16.5944 8.87222 16.9849 9.26274L22.5151 14.793C22.9057 15.1835 22.9057 15.8167 22.5151 16.2072L16.9849 21.7374C16.5944 22.1279 15.9612 22.1279 15.5707 21.7374L10.0405 16.2072C9.64993 15.8167 9.64993 15.1835 10.0405 14.793L15.5707 9.26274ZM13.8588 15.5001L16.2778 13.0811L18.6968 15.5001L16.2778 17.919L13.8588 15.5001Z" class="svgFill"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.5 29.0001C7.98528 29.0001 10 26.9853 10 24.5001C10 22.0148 7.98528 20.0001 5.5 20.0001C3.01472 20.0001 1 22.0148 1 24.5001C1 26.9853 3.01472 29.0001 5.5 29.0001ZM5.5 25.8001C6.21797 25.8001 6.8 25.218 6.8 24.5001C6.8 23.7821 6.21797 23.2001 5.5 23.2001C4.78203 23.2001 4.2 23.7821 4.2 24.5001C4.2 25.218 4.78203 25.8001 5.5 25.8001Z" class="svgFill"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M26.5 29.0001C28.9853 29.0001 31 26.9853 31 24.5001C31 22.0148 28.9853 20.0001 26.5 20.0001C24.0147 20.0001 22 22.0148 22 24.5001C22 26.9853 24.0147 29.0001 26.5 29.0001ZM26.5 25.8001C27.218 25.8001 27.8 25.218 27.8 24.5001C27.8 23.7821 27.218 23.2001 26.5 23.2001C25.782 23.2001 25.2 23.7821 25.2 24.5001C25.2 25.218 25.782 25.8001 26.5 25.8001Z" class="svgFill"/>
<path d="M24.2215 3.57582C25.8594 1.93791 28.66 3.09794 28.66 5.41429V18.2779H25.46V6.86282L21.9413 10.3815L19.6786 8.11871L24.2215 3.57582Z" class="svgFill"/>
</g>
</g>
</svg>
`;

const marketplace = `<svg 
    class="serviceIcon" 
    role="img" 
    aria-labelledby="menu-text-marketplace" 
    alt="marketplace" 
    title="marketplace"
    width="15" 
    height="15" 
    viewBox="0 0 15 15" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg">
    <path 
        d="M14.921 5.41359L14.1335 2.13609C13.9685 1.46109 13.3835 0.996094 12.701 0.996094H2.28348C1.60848 0.996094 1.01598 1.46859 0.858477 2.13609L0.0709766 5.41359C-0.109023 6.17859 0.0559767 6.95859 0.535977 7.57359C0.595977 7.65609 0.678477 7.71609 0.745977 7.79109V12.9961C0.745977 13.8211 1.42098 14.4961 2.24598 14.4961H12.746C13.571 14.4961 14.246 13.8211 14.246 12.9961V7.79109C14.3135 7.72359 14.396 7.65609 14.456 7.58109C14.936 6.96609 15.1085 6.17859 14.921 5.41359ZM12.6785 2.48859L13.466 5.76609C13.541 6.08109 13.4735 6.39609 13.2785 6.64359C13.1735 6.77859 12.9485 6.99609 12.5735 6.99609C12.116 6.99609 11.7185 6.62859 11.666 6.14109L11.231 2.49609L12.6785 2.48859ZM8.24598 2.49609H9.71598L10.121 5.88609C10.1585 6.17859 10.0685 6.47109 9.87348 6.68859C9.70848 6.88359 9.46848 6.99609 9.16098 6.99609C8.65848 6.99609 8.24598 6.55359 8.24598 6.01359V2.49609ZM4.86348 5.88609L5.27598 2.49609H6.74598V6.01359C6.74598 6.55359 6.33348 6.99609 5.77848 6.99609C5.52348 6.99609 5.29098 6.88359 5.11098 6.68859C4.92348 6.47109 4.83348 6.17859 4.86348 5.88609ZM1.52598 5.76609L2.28348 2.49609H3.76098L3.32598 6.14109C3.26598 6.62859 2.87598 6.99609 2.41848 6.99609C2.05098 6.99609 1.81848 6.77859 1.72098 6.64359C1.51848 6.40359 1.45098 6.08109 1.52598 5.76609ZM2.24598 12.9961V8.47359C2.30598 8.48109 2.35848 8.49609 2.41848 8.49609C3.07098 8.49609 3.66348 8.22609 4.09848 7.78359C4.54848 8.23359 5.14848 8.49609 5.83098 8.49609C6.48348 8.49609 7.06848 8.22609 7.50348 7.79859C7.94598 8.22609 8.54598 8.49609 9.22098 8.49609C9.85098 8.49609 10.451 8.23359 10.901 7.78359C11.336 8.22609 11.9285 8.49609 12.581 8.49609C12.641 8.49609 12.6935 8.48109 12.7535 8.47359V12.9961H2.24598Z" 
        class="svgFill"
        stroke="none"
        stroke-width="0"/>
</svg>
`;

const more = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-more" width="12" height="4" viewBox="0 0 12 4" fill="none" xmlns="http://www.w3.org/2000/svg" alt="more" title="more">
<path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 0.5C0.675 0.5 0 1.175 0 2C0 2.825 0.675 3.5 1.5 3.5C2.325 3.5 3 2.825 3 2C3 1.175 2.325 0.5 1.5 0.5ZM10.5 0.5C9.675 0.5 9 1.175 9 2C9 2.825 9.675 3.5 10.5 3.5C11.325 3.5 12 2.825 12 2C12 1.175 11.325 0.5 10.5 0.5ZM4.5 2C4.5 1.175 5.175 0.5 6 0.5C6.825 0.5 7.5 1.175 7.5 2C7.5 2.825 6.825 3.5 6 3.5C5.175 3.5 4.5 2.825 4.5 2Z" class="svgFill"/>
</svg>
`;

const orchestrator = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-orchestrator" width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="orchestrator" title="orchestrator">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.1705 0.341013C3.066 0.132014 2.85239 -3.52998e-06 2.61872 0C2.27802 5.14743e-06 2.00183 0.276198 2.00183 0.616897L2.00184 8.39393C2.00184 8.44092 2.00719 8.48667 2.01731 8.53059C2.00704 8.68575 2.00182 8.8423 2.00182 9.00006C2.00182 10.6072 2.5434 12.0878 3.45401 13.2693L0.37484 14.8501C0.148443 14.9406 -1.14441e-05 15.1599 -1.14441e-05 15.4037C-1.14441e-05 15.7331 0.266968 16 0.596304 16H8.39578C8.44277 16 8.48851 15.9947 8.53242 15.9846C8.68756 15.9948 8.84408 16.0001 9.00182 16.0001C10.6098 16.0001 12.0912 15.4579 13.2731 14.5463L14.8288 17.6577C14.9337 17.8675 15.1481 18 15.3827 18C15.7246 18 16.0019 17.7228 16.0019 17.3808V9.6061C16.0019 9.55902 15.9965 9.51319 15.9863 9.4692C15.9966 9.31414 16.0018 9.15771 16.0018 9.00006C16.0018 7.39117 15.459 5.90905 14.5466 4.72684L17.6601 3.17078C17.8695 3.06611 18.0018 2.85209 18.0018 2.61797C18.0018 2.27666 17.7251 1.99998 17.3838 1.99998H9.60798C9.56085 1.99998 9.51498 2.00535 9.47094 2.01553C9.31589 2.00527 9.15947 2.00006 9.00182 2.00006C7.39261 2.00006 5.91023 2.54306 4.7279 3.45584L3.1705 0.341013ZM9.00182 14.0001C11.7632 14.0001 14.0018 11.7615 14.0018 9.00006C14.0018 6.23864 11.7632 4.00006 9.00182 4.00006C6.2404 4.00006 4.00182 6.23864 4.00182 9.00006C4.00182 11.7615 6.2404 14.0001 9.00182 14.0001ZM10.0018 9.00006C10.0018 9.55235 9.55412 10.0001 9.00183 10.0001C8.44955 10.0001 8.00183 9.55235 8.00183 9.00006C8.00183 8.44778 8.44955 8.00006 9.00183 8.00006C9.55412 8.00006 10.0018 8.44778 10.0018 9.00006ZM12.0018 9.00006C12.0018 10.6569 10.6587 12.0001 9.00183 12.0001C7.34498 12.0001 6.00183 10.6569 6.00183 9.00006C6.00183 7.34321 7.34498 6.00006 9.00183 6.00006C10.6587 6.00006 12.0018 7.34321 12.0018 9.00006Z" class="svgFill"/>
</svg>
`;

const processes = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-processes" width="18px" height="18px" viewBox="0 0 18 18" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" alt="processes" title="processes">
      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g class="svgStroke" stroke-width="2">
              <g transform="translate(1.000000, 1.000000)">
                  <polygon points="0 0 7 0 7 7 0 7"></polygon>
                  <path d="M16,12.5 C16,14.433 14.433,16 12.5,16 C10.567,16 9,14.433 9,12.5 C9,10.567 10.567,9 12.5,9 C14.433,9 16,10.567 16,12.5 Z"></path>
                  <polyline points="1 10 1 15.25 6.25 15.25"></polyline>
                  <polyline points="15 6 15 0.75 9.75 0.75"></polyline>
              </g>
          </g>
      </g>
  </svg>
`;

const processmining = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-processmining" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="process mining" title="process mining">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M8 9V7H10V9H8Z" class="svgFill"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M8 18V16H10V18H8Z" class="svgFill"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M16 11H2V13H16V11ZM0 9V15H18V9H0Z" class="svgFill"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M13 2H5V4H13V2ZM3 0V6H15V0H3Z" class="svgFill"/>
</svg>
`;

const reinfer = `<svg 
    role="img"
    class="serviceIcon" 
    aria-labelledby="menu-text-reinfer" 
    width="18"
    height="18" 
    viewBox="0 0 18 18" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    alt="re:infer" 
    title="re:infer">
    <g clip-path="url(#clip0_1110_208514)">
        <path 
            stroke="none"
            stroke-width="0"
            fill-rule="evenodd" 
            clip-rule="evenodd" 
            d="M11.3285 14.4142C10.5474 13.6332 10.5474 12.3668 11.3285 11.5858C12.1095 10.8047 13.3758 10.8047 14.1569 11.5858C14.9379 12.3668 14.9379 13.6332 14.1569 14.4142C13.3758 15.1953 12.1095 15.1953 11.3285 14.4142ZM9.91424 10.1716C8.35215 11.7337 8.35215 14.2663 9.91424 15.8284C11.2322 17.1464 13.241 17.3524 14.7749 16.4464L16.2782 17.9498L17.6924 16.5355L16.1891 15.0322C17.095 13.4983 16.889 11.4895 15.5711 10.1716C14.009 8.60948 11.4763 8.60948 9.91424 10.1716Z" 
            class="svgFill" />
        <path 
            stroke="none"
            stroke-width="0"
            fill-rule="evenodd" 
            clip-rule="evenodd" 
            d="M3.81598 4.55218L3.59722 4.71215L3.37848 4.87212L3.15973 5.03209L1.40973 6.31185L1.00002 6.61146L1.00002 7.11904L1.00002 16.6718L1.00002 17.6718L2.00002 17.6718L10.9574 17.6718C10.3195 17.4285 9.72135 17.0498 9.20712 16.5355C8.93986 16.2683 8.70918 15.9783 8.51508 15.6718L3.00002 15.6718V8.81687L7.95267 11.5627C8.14128 10.9322 8.45692 10.3315 8.8996 9.80092L3.8529 7.00291L4.34031 6.64647L4.55906 6.4865L4.77781 6.32653L4.99656 6.16656L3.81598 4.55218ZM12.316 8.01813C13.7338 7.89744 15.1933 8.37955 16.2782 9.46447C16.5593 9.74557 16.7999 10.0517 17 10.3758L17 7.11904L17 6.61146L16.5903 6.31185L15.7153 5.67197L15.2778 5.35203L14.8403 5.03209L14.4028 4.71215L14.1841 4.55218L13.0035 6.16656L13.2222 6.32653L13.6597 6.64647L14.0972 6.96641L14.1471 7.00291L12.316 8.01813ZM16.5561 17.6718H16.0002L14.7748 16.4464C13.2409 17.3524 11.2322 17.1464 9.91423 15.8284C9.86303 15.7772 9.81352 15.725 9.76568 15.6718H15L15 9.69697C15.2011 9.83475 15.3924 9.99295 15.5711 10.1716C16.889 11.4895 17.095 13.4983 16.1891 15.0322L17 15.8432V16.6718L17 17.2279L16.5561 17.6718ZM14.1857 9.26832C12.7543 8.71567 11.0691 9.01675 9.91423 10.1716C9.37348 10.7123 9.01992 11.3694 8.85355 12.0622L9.00002 12.1434L9.4849 11.8746L12.9849 9.9341L14.1857 9.26832ZM14.586 17.6718H14.527C14.5412 17.6664 14.5554 17.6609 14.5695 17.6553L14.586 17.6718Z" 
            class="svgFill" />
        <path 
            d="M14 8V1H4V9" 
            class="svgStroke" 
            stroke-width="2"/>
        <rect x="6" y="5" width="1" height="2" class="svgFill" stroke="none" stroke-width="0"/>
        <rect x="8" y="6" width="1" height="1" class="svgFill" stroke="none" stroke-width="0"/>
        <rect x="7" y="5" width="1" height="1" class="svgFill" stroke="none" stroke-width="0"/>
        <path 
            fill-rule="evenodd" 
            clip-rule="evenodd" 
            d="M9 3H6V5H7V4H8V5H9V3Z" 
            class="svgFill" 
            stroke="none" 
            stroke-width="0" />
        <rect x="10" y="6" width="1" height="1" class="svgFill" stroke="none" stroke-width="0"/>
        <rect x="10" y="4" width="1" height="1" class="svgFill" stroke="none" stroke-width="0"/>
    </g>
    <defs>
        <clipPath id="clip0_1110_208514">
            <rect width="18" height="18" class="fillBg" stroke="none" stroke-width="0"/>
        </clipPath>
    </defs>
</svg>
`;

const roboticsops = `<svg class="serviceIcon" aria-labelledby="menu-text-roboticsops" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="automation ops" title="automation ops">
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.0298 2.82083C12.2743 3.28275 12.406 3.78877 12.4248 4.29818L13.4073 4.5722L12.8897 6.50395L11.9025 6.25016C11.7682 6.46361 11.6081 6.66548 11.4222 6.85134C11.2364 7.03722 11.0344 7.19732 10.821 7.33162L11.0748 8.31888L9.14301 8.83649L8.86896 7.85384C8.3596 7.83504 7.85362 7.70338 7.39174 7.45887L6.66376 8.17218L5.24962 6.75804L5.9629 6.03009C5.71836 5.5682 5.58668 5.0622 5.56787 4.55282L4.58531 4.2788L5.10292 2.34704L6.09005 2.60081C6.22436 2.3873 6.38447 2.18537 6.57037 1.99946C6.75624 1.81359 6.95814 1.65351 7.17161 1.51921L6.91786 0.532111L8.84961 0.0145L9.1236 0.996957C9.63303 1.01575 10.1391 1.14743 10.601 1.392L11.3289 0.678812L12.743 2.09295L12.0298 2.82083ZM10.2092 5.63859C9.5393 6.30849 8.45317 6.30849 7.78327 5.63859C7.11337 4.96869 7.11337 3.88256 7.78327 3.21265C8.45317 2.54275 9.5393 2.54275 10.2092 3.21265C10.8791 3.88256 10.8791 4.96869 10.2092 5.63859ZM12.0305 11.9224C12.2753 12.3847 12.4072 12.8912 12.426 13.401L13.4089 13.6751L12.8912 15.6069L11.904 15.3531C11.7696 15.5669 11.6093 15.7692 11.4231 15.9553C11.237 16.1414 11.035 16.3016 10.8213 16.436L11.0751 17.4231L9.1433 17.9407L8.86933 16.9583C8.35928 16.9395 7.85261 16.8077 7.39016 16.5627L6.66233 17.2759L5.24819 15.8618L5.96149 15.1338C5.71668 14.6714 5.58493 14.1648 5.56624 13.6549L4.58342 13.3808L5.10103 11.449L6.08873 11.7029C6.22308 11.4894 6.38324 11.2874 6.56919 11.1014C6.75529 10.9154 6.95743 10.7551 7.17117 10.6207L6.91722 9.63284L8.84898 9.11523L9.1232 10.0985C9.63295 10.1173 10.1393 10.249 10.6015 10.4937L11.3299 9.78L12.7441 11.1941L12.0305 11.9224ZM10.2096 14.7414C9.5394 15.4115 8.45282 15.4115 7.78264 14.7414C7.11245 14.0712 7.11245 12.9846 7.78264 12.3144C8.45282 11.6442 9.5394 11.6442 10.2096 12.3144C10.8798 12.9846 10.8798 14.0712 10.2096 14.7414ZM15.1642 9.11676L13.0429 6.99544L14.4571 5.58122L17.2855 8.40965L17.9926 9.11676L17.2855 9.82386L14.4571 12.6523L13.0429 11.2381L15.1642 9.11676ZM4.9571 11.2382L2.83577 9.11689L4.9571 6.99557L3.54288 5.58135L0.714454 8.40978L0.00734763 9.11689L0.714454 9.82399L3.54288 12.6524L4.9571 11.2382Z" class="svgFill"/>
</svg>
`;

const studio = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-studio" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" alt="studio" title="studio">
    <path class="svgFill" d="M12.9995 1.89975C12.9995 2.18155 12.9115 2.45702 12.7467 2.69132C12.5819 2.92563 12.3476 3.10825 12.0735 3.21609C11.7994 3.32393 11.4978 3.35215 11.2069 3.29717C10.9159 3.24219 10.6486 3.1065 10.4389 2.90723C10.2291 2.70797 10.0862 2.4541 10.0283 2.17771C9.97046 1.90133 10.0002 1.61485 10.1137 1.3545C10.2272 1.09415 10.4195 0.871627 10.6662 0.715068C10.9128 0.558508 11.2028 0.474945 11.4995 0.474945C11.8973 0.474945 12.2789 0.625058 12.5602 0.89226C12.8415 1.15946 12.9995 1.52187 12.9995 1.89975Z"/>
    <path class="svgFill" d="M8.00024 16.1478C8.00024 16.4296 7.91227 16.7051 7.74745 16.9394C7.58263 17.1737 7.34836 17.3563 7.07427 17.4641C6.80018 17.572 6.49858 17.6002 6.20761 17.5452C5.91664 17.4902 5.64936 17.3545 5.43958 17.1553C5.22981 16.956 5.08694 16.7021 5.02907 16.4258C4.97119 16.1494 5.00089 15.8629 5.11443 15.6025C5.22796 15.3422 5.42022 15.1197 5.66689 14.9631C5.91356 14.8066 6.20357 14.723 6.50024 14.723C6.89807 14.723 7.2796 14.8731 7.5609 15.1403C7.84221 15.4075 8.00024 15.7699 8.00024 16.1478Z"/>
    <path class="svgFill" d="M12.9995 11.8734C12.9995 11.1176 12.6834 10.3928 12.1208 9.8584C11.5582 9.32399 10.7952 9.02377 9.99951 9.02377V6.17416C11.5908 6.17416 13.1169 6.77461 14.2422 7.84342C15.3674 8.91223 15.9995 10.3618 15.9995 11.8734C15.9995 13.3849 15.3674 14.8345 14.2422 15.9033C13.1169 16.9721 11.5908 17.5726 9.99951 17.5726V14.723C10.7952 14.723 11.5582 14.4227 12.1208 13.8883C12.6834 13.3539 12.9995 12.6291 12.9995 11.8734Z"/>
    <path class="svgFill" d="M5 6.17415C5 6.92991 5.31607 7.65472 5.87868 8.18912C6.44129 8.72353 7.20435 9.02375 8 9.02375V11.8734C6.4087 11.8734 4.88258 11.2729 3.75736 10.2041C2.63214 9.13529 2 7.68567 2 6.17415C2 4.66263 2.63214 3.21301 3.75736 2.1442C4.88258 1.0754 6.4087 0.474945 8 0.474945V3.32455C7.20435 3.32455 6.44129 3.62477 5.87868 4.15918C5.31607 4.69358 5 5.41839 5 6.17415Z" fill="#526069"/>
</svg>
`;

const taskmining = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-taskmining" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="task mining" title="task mining">
    <path d="M2 12V16H6.66667H11.3333H16V12M16 6V2H11.3333H6.66667H2V6" class="svgStroke" stroke-width="2"/>
    <path d="M2.46221 9L9 6.09432L15.5378 9L9 11.9057L2.46221 9Z" class="svgStroke" stroke-width="2"/>
</svg>
`;

const testmanager = `<svg class="serviceIcon" role="img" aria-labelledby="menu-text-testmanager" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" alt="test manager" title="test manager">
    <path d="M5.34375 16.875H1.125V2.25H13.5V4.78125" class="svgStroke" stroke-width="2"/>
    <rect x="7.875" y="6.75" width="9" height="10.125" class="svgStroke" stroke-width="2"/>
    <rect x="10.125" y="9" width="4.5" height="2.25" class="svgFill"/>
    <rect x="10.125" y="12.375" width="2.25" height="2.25" class="svgFill"/>
    <rect x="5.0625" width="4.5" height="4.5" class="svgFill"/>
</svg>
`;

const vpnadmin = `<svg class="serviceIcon" width="18" height="18" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" alt="VPN Gateway" title="VPN Gateway">
<g clip-path="url(#clip0_5147_328120)">
<rect x="8" y="16" width="25" height="20" class="svgStroke" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
<path fill-rule="evenodd" clip-rule="evenodd" class="svgFill" d="M17.5 24C17.5 22.3431 18.8431 21 20.5 21C22.1569 21 23.5 22.3431 23.5 24C23.5 25.105 22.9025 26.0705 22.013 26.5911L22.5 30H18.5L18.987 26.5911C18.0975 26.0705 17.5 25.105 17.5 24Z" />
<path d="M26 14V10C26 6.68629 23.3137 4 20 4V4C16.6863 4 14 6.68629 14 10V14" class="svgStroke" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
</g>
<defs>
<clipPath id="clip0_5147_328120">
<rect width="40" height="40" class="svgFill"/>
</clipPath>
</defs>
</svg>
`;

const ICON_MAP = [
    {
        id: 'actions',
        svg: actions,
        name: 'Actions',
    },
    {
        id: 'academy',
        svg: academy,
        name: 'Academy',
    },
    {
        id: 'admin',
        svg: admin,
    },
    {
        id: 'aifabric',
        svg: aifabric,
        name: 'AI Center',
    },
    {
        id: 'apps',
        svg: apps,
        name: 'Apps',
    },
    {
        id: 'automationhub',
        svg: automationhub,
        name: 'Automation Hub',
    },
    {
        id: 'reinfer',
        svg: reinfer,
        name: 'Communications Mining',
    },
    {
        id: 'roboticsops',
        svg: roboticsops,
        name: 'Automation Ops',
    },
    {
        id: 'du',
        svg: du,
        name: 'Document Understanding',
    },
    {
        id: 'automationstore',
        svg: automationstore,
        name: 'Automation Store',
    },
    {
        id: 'chatbots',
        svg: chatbots,
        name: 'Chatbots',
    },
    {
        id: 'customerportal',
        svg: customerPortal,
        name: 'Customer Portal',
    },
    {
        id: 'dataservice',
        svg: dataservice,
        name: 'Data Service',
    },
    {
        id: 'insights',
        svg: insights,
        name: 'Insights',
    },
    {
        id: 'marketplace',
        svg: marketplace,
        name: 'Marketplace',
    },
    {
        id: 'help',
        svg: help,
        name: 'Help',
    },
    {
        id: 'home',
        svg: home,
        name: 'Home',
    },
    {
        id: 'orchestrator',
        svg: orchestrator,
        name: 'Orchestrator',
    },
    {
        id: 'processes',
        svg: processes,
        name: 'Process Launcher',
    },
    {
        id: 'taskmining',
        svg: taskmining,
        name: 'Task Mining',
    },
    {
        id: 'processmining',
        svg: processmining,
        name: 'Process Mining',
    },
    {
        id: 'studio',
        svg: studio,
        name: 'Studio',
    },
    {
        id: 'testmanager',
        svg: testmanager,
        name: 'Test Manager',
    },
    {
        id: 'integrations',
        svg: integrations,
        name: 'Connections',
    },
    {
        id: 'more',
        svg: more,
    },
    {
        id: 'vpn gateway',
        svg: vpnadmin,
    },
    {
        id: 'cloud robots',
        name: 'Cloud Robots',
        svg: vpnadmin,
    },
    {
        id: 'hypervisor',
        name: 'Cloud Robots',
    },
    {
        id: 'portal',
        name: 'Automation Cloud',
    },
    {
        id: 'docs',
        name: 'Documentation',
        svg: docs,
    },
    {
        id: 'agents',
        name: 'Agent Service',
        svg: agents,
    },
    {
        id: 'assistant',
        name: 'Assistant',
        svg: assistant,
    },
    {
        id: 'agenticorchestration',
        name: 'Agentic Orchestration',
        svg: agenticorchestration,
    },
    {
        id: 'maestro',
        name: 'Maestro',
        svg: maestro,
    },
];
const getSvg = (key) => {
    var _a;
    const svg = (_a = ICON_MAP.find(serviceMapping => { var _a; return serviceMapping.id === key || ((_a = serviceMapping.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === key; })) === null || _a === void 0 ? void 0 : _a.svg;
    return svg ? svg : aifabric;
};
const getTitle = (key) => {
    var _a;
    const title = (_a = ICON_MAP.find(serviceMapping => { var _a; return serviceMapping.id === key || ((_a = serviceMapping.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === key; })) === null || _a === void 0 ? void 0 : _a.name;
    return title ? title : key;
};
const idToSvg = ICON_MAP.reduce((accum, curr) => (Object.assign(Object.assign({}, accum), { [curr.id]: curr.svg })), {});

export { AccountAndTenants as A, EventHandlersInstanceResolver as E, ICON_MAP as I, SettingsInstanceResolver as S, accountAndTenantsStore as a, Settings as b, getCobrandingLogoProperty as c, getTitle as d, getSvg as g, idToSvg as i };

//# sourceMappingURL=serviceIconHelper-70cacacd.js.map